import { Button, Input, InputWrapper } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import {
  Box,
  Collapse,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

export type ExpenseAttachmentFormValues = {
  filename: string;
  isOwnerAccessible: boolean;
};

type Props = {
  opened: boolean;
  file: File | null;
  initialValues?: ExpenseAttachmentFormValues;
  title?: string;
  owners: string[];
  loading?: boolean;
  onClose: () => void;
  onSubmit: (values: ExpenseAttachmentFormValues) => Promise<void>;
};

const trapSpacesForRequiredFields = (value: string | undefined) =>
  !!value?.trim() || false;

export const ExpenseAttachmentModal = ({
  opened,
  file,
  initialValues,
  title,
  owners,
  loading = false,
  onClose,
  onSubmit,
}: Props) => {
  const theme = useMantineTheme();

  const resolvedValues = useMemo(
    () =>
      initialValues ?? {
        filename: file?.name ?? '',
        isOwnerAccessible: false,
      },
    [initialValues?.filename, initialValues?.isOwnerAccessible, file?.name]
  );

  const methods = useForm<ExpenseAttachmentFormValues>({
    defaultValues: resolvedValues,
  });

  const { control, handleSubmit, formState, reset } = methods;

  const isOwnerAccessible = useWatch({
    control,
    name: 'isOwnerAccessible',
  });

  useEffect(() => {
    if (!opened) return;
    reset(resolvedValues);
  }, [resolvedValues, reset, opened]);

  return (
    <Modal
      title={title ?? (initialValues ? 'Edit file' : 'Add file')}
      opened={opened}
      onClose={onClose}
      size={520}
      centered
      withinPortal
      styles={{
        title: {
          fontWeight: 600,
        },
      }}
    >
      <Box>
        <Stack gap="md">
          <Controller
            name="filename"
            control={control}
            rules={{
              required: 'File name is required',
              validate: trapSpacesForRequiredFields,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="File name"
                required
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  type="text"
                  placeholder="Enter file name"
                  error={!!fieldState.error}
                  data-autofocus
                  disabled={field.disabled || !!initialValues?.filename}
                />
              </InputWrapper>
            )}
          />

          <Controller
            name="isOwnerAccessible"
            control={control}
            render={({ field }) => (
              <Group gap="sm">
                <Switch
                  checked={field.value}
                  onChange={(event) =>
                    field.onChange(event.currentTarget.checked)
                  }
                />
                <Text size="sm">Share with listing owners</Text>
              </Group>
            )}
          />

          <Collapse in={isOwnerAccessible}>
            <Box
              bg={theme.colors.blue[0]}
              p="sm"
              sx={(theme) => ({
                border: `1px solid ${theme.colors.blue[1]}`,
                borderRadius: theme.radius.md,
              })}
            >
              <Group align="flex-start" gap="sm" wrap="nowrap">
                <Icon icon="InfoIcon" size={16} />
                <Stack gap={4}>
                  <Text size="sm" c="neutral.7">
                    Visibility is based on the owner list and updates
                    automatically when owners change.
                  </Text>
                  {owners.length > 0 && (
                    <Text size="sm" c="neutral.7">
                      Shared with:{' '}
                      <Text span fw={500}>
                        {owners.join(', ')}
                      </Text>
                    </Text>
                  )}
                </Stack>
              </Group>
            </Box>
          </Collapse>
        </Stack>

        <Group justify="flex-end" gap="sm" mt="lg">
          <Button
            type="button"
            onClick={() => {
              reset({
                filename: '',
                isOwnerAccessible: false,
              });
              onClose();
            }}
            disabled={loading || formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            loading={loading || formState.isSubmitting}
          >
            Save file
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
